<template>
    <v-card flat>
        <v-overlay :value="loading || saving || loadingFromRoute">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <template v-if="activeUnmatchedClient && activeUnmatchedClient.id">

        <v-toolbar color="bal-gray" dense flat>
            <v-toolbar-title>{{ activeUnmatchedClient.name || activeUnmatchedClient.code }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn @click="closeDrawer" icon>
                <v-icon>close</v-icon>
            </v-btn>
        </v-toolbar>

        <div class="pa-4">
            <!-- Record info -->
            <v-row no-gutters>
                <v-col class="row-title" cols="3">{{ "Code" | t }}</v-col>
                <v-col cols="6">{{ activeUnmatchedClient.code || "-" }}</v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="row-title" cols="3">{{ "Name" | t }}</v-col>
                <v-col cols="6">{{ activeUnmatchedClient.name || "-" }}</v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="row-title" cols="3">{{ "Type" | t }}</v-col>
                <v-col cols="6">{{ formatType(activeUnmatchedClient.type) }}</v-col>
            </v-row>
            <v-row no-gutters>
                <v-col class="row-title" cols="3">{{ "Source" | t }}</v-col>
                <v-col cols="6">{{ activeUnmatchedClient.source === 'invoice' ? 'Odoo' : 'Import' }}</v-col>
            </v-row>
            <v-row no-gutters v-if="activeUnmatchedClient.address">
                <v-col class="row-title" cols="3">{{ "Address" | t }}</v-col>
                <v-col cols="6">{{ activeUnmatchedClient.address }}</v-col>
            </v-row>

            <v-divider class="my-3" />

            <!-- Tabs for Match / Create -->
            <v-tabs v-model="activeMode" color="bal-red" slider-color="bal-red" height="32" class="mb-3">
                <v-tab key="match">{{ "Match with existing" | t }}</v-tab>
                <v-tab key="create">{{ "Create new client" | t }}</v-tab>
            </v-tabs>

            <!-- Match with existing client -->
            <div v-if="activeMode === 0">
                <v-row no-gutters class="pb-0 mb-0">
                    <v-col class="mt-2 row-title" cols="3">{{ "Select client" | t }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="selectedClient"
                            :items="clients"
                            :loading="loadingClients"
                            item-value="id"
                            :placeholder="'Start typing to Search' | t"
                            return-object
                            dense
                            hide-details
                            outlined
                            clearable
                            class="text-field-item mb-1"
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ item.chain_name }} - {{ item.shop_name }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                            <template v-slot:selection="{ item }">
                                {{ item.name }}
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
                <v-row no-gutters class="mt-3">
                    <v-spacer />
                    <v-btn
                        color="bal-red"
                        dark
                        :disabled="!selectedClient"
                        :loading="saving"
                        @click="matchClient"
                        class="mr-2"
                    >
                        <v-icon left>link</v-icon>
                        {{ "Match" | t }}
                    </v-btn>
                </v-row>
            </div>

            <!-- Create new client -->
            <div v-else>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Name" | t }} *</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.name"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Chain name" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.chain_name"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Shop name" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.shop_name"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Business type" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.business_type"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Address" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.shop_address"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "City" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.shop_place"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "County" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.shop_county"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Country" | t }}</v-col>
                    <v-col cols="6">
                        <v-autocomplete
                            v-model="newClient.shop_country_code"
                            :items="countries"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            hide-no-data
                            hide-selected
                            :item-text="item => (item.name_et || item.name_en) + ' (' + item.code + ')'"
                            item-value="code"
                            outlined
                            :placeholder="t('Start typing to Search')"
                        >
                            <template slot="selection" slot-scope="{ item }">
                                {{ item.name_et || item.name_en }} ({{ item.code }})
                            </template>
                            <template slot="item" slot-scope="{ item }">
                                {{ item.name_et || item.name_en }} ({{ item.code }})
                            </template>
                        </v-autocomplete>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="mt-2 row-title" cols="3">{{ "Price list" | t }}</v-col>
                    <v-col cols="6">
                        <v-text-field
                            v-model="newClient.price_list"
                            class="text-field-item mb-1"
                            dense
                            hide-details
                            outlined
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters class="mt-3">
                    <v-spacer />
                    <v-btn
                        color="bal-red"
                        dark
                        :disabled="!newClient.name"
                        :loading="saving"
                        @click="createAndMatchClient"
                        class="mr-2"
                    >
                        <v-icon left>add</v-icon>
                        {{ "Create and match" | t }}
                    </v-btn>
                </v-row>
            </div>
        </div>
        </template>
    </v-card>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import Store from "../../store";
import axios from "axios";
import settings from "@/settings";

export default {
    name: "unmatched-client-view",
    data() {
        return {
            selectedClient: null,
            loading: false,
            saving: false,
            loadingClients: false,
            activeMode: 0,
            newClient: {
                name: "",
                chain_name: "",
                shop_name: "",
                business_type: "",
                shop_address: "",
                shop_place: "",
                shop_county: "",
                shop_country_code: "EE",
                price_list: ""
            },
            loadingFromRoute: false
        };
    },
    computed: {
        ...mapState("UnmatchedShopsStore", ["activeUnmatchedClient", "clients", "unmatchedClients"]),
        ...mapState(["countries"])
    },
    async mounted() {
        // Ensure clients tab is active
        Store.commit("UnmatchedShopsStore/setActiveTab", "clients");

        // Load client from route params if not already set (e.g., on page refresh)
        if (this.$route.params.recordId && (!this.activeUnmatchedClient || !this.activeUnmatchedClient.id)) {
            this.loadingFromRoute = true;
            // First ensure the list is loaded
            if (!this.unmatchedClients || this.unmatchedClients.length === 0) {
                await Store.dispatch("UnmatchedShopsStore/getUnmatchedClientsList");
            }
            // Find the client in the list
            const recordId = parseInt(this.$route.params.recordId);
            const source = this.$route.params.source;
            const client = this.unmatchedClients.find(
                c => parseInt(c.id) === recordId && c.source === source
            );
            if (client) {
                this.setActiveUnmatchedClient(client);
            }
            this.loadingFromRoute = false;
        }
    },
    watch: {
        activeUnmatchedClient: {
            handler(newVal) {
                if (newVal && newVal.id) {
                    this.prefillNewClient();
                }
            },
            immediate: true
        }
    },
    methods: {
        ...mapMutations("UnmatchedShopsStore", ["setActiveUnmatchedClient"]),
        formatType(type) {
            if (!type) return "-";
            return (type.charAt(0).toUpperCase() + type.slice(1)).replaceAll("_", " ");
        },
        prefillNewClient() {
            const record = this.activeUnmatchedClient;
            if (!record) return;

            // Eugesta/Smarten are always Estonia
            const isEstonia = record.type !== 'odoo';

            this.newClient = {
                name: record.name || "",
                chain_name: record.main_customer_name || record.name || "",
                shop_name: record.name || "",
                business_type: isEstonia ? "eesti" : "",
                shop_address: record.address || "",
                shop_place: record.city || "",
                shop_county: record.county || "",
                shop_country_code: isEstonia ? "EE" : "",
                price_list: ""
            };
        },
        closeDrawer() {
            this.setActiveUnmatchedClient({});
            this.selectedClient = null;
            this.activeMode = 0;
            this.resetNewClient();
            this.$router.push({ name: "unmatched-shops-list" });
        },
        resetNewClient() {
            this.newClient = {
                name: "",
                chain_name: "",
                shop_name: "",
                business_type: "",
                shop_address: "",
                shop_place: "",
                shop_county: "",
                shop_country_code: "EE",
                price_list: ""
            };
        },
        async matchClient() {
            if (!this.selectedClient) return;

            this.saving = true;
            try {
                const response = await axios.post(settings.api_url + "/unmatched-client/", {
                    record_id: this.activeUnmatchedClient.id,
                    source: this.activeUnmatchedClient.source,
                    client_id: this.selectedClient.id,
                    customer_code: this.activeUnmatchedClient.code,
                    type: this.activeUnmatchedClient.type
                });

                if (response.data.success === 1) {
                    await Store.dispatch("UnmatchedShopsStore/getUnmatchedClientsList");
                    this.closeDrawer();
                    Store.commit("UnmatchedShopsStore/setRequestSuccess", true);
                } else {
                    alert(this.t("Match failed") + ": " + (response.data.error || "Unknown error"));
                }
            } catch (e) {
                console.error(e);
                alert(this.t("Match failed"));
            }
            this.saving = false;
        },
        async createAndMatchClient() {
            if (!this.newClient.name) return;

            this.saving = true;
            try {
                const response = await axios.post(settings.api_url + "/unmatched-client/", {
                    create_new: true,
                    record_id: this.activeUnmatchedClient.id,
                    source: this.activeUnmatchedClient.source,
                    customer_code: this.activeUnmatchedClient.code,
                    type: this.activeUnmatchedClient.type,
                    client_data: this.newClient
                });

                if (response.data.success === 1) {
                    await Store.dispatch("UnmatchedShopsStore/getUnmatchedClientsList");
                    this.closeDrawer();
                    Store.commit("UnmatchedShopsStore/setRequestSuccess", true);
                } else {
                    alert(this.t("Create failed") + ": " + (response.data.error || "Unknown error"));
                }
            } catch (e) {
                console.error(e);
                alert(this.t("Create failed"));
            }
            this.saving = false;
        }
    }
};
</script>

<style scoped>
.row-title {
    color: gray;
    padding-top: 4px;
    padding-bottom: 4px;
    font-size: 13px;
}

.text-field-item {
    font-size: 13px;
}

::v-deep .v-tabs .v-tab {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
    font-size: 13px;
    min-width: 0;
}
</style>
